import debug from 'debug';

const log = debug('rois:offline:persistent-storage');

/**
 * Pide al navegador que NO desaloje nuestro almacenamiento.
 *
 * ── El problema ───────────────────────────────────────────────────────────
 * Por defecto todo origen web vive en modo *best-effort*: el navegador puede
 * borrar su IndexedDB cuando el disco se llena, o simplemente cuando el sitio
 * lleva tiempo sin usarse. La especificación de almacenamiento lo dice sin
 * rodeos — el desalojo «skips over origins that have been granted data
 * persistence by using navigator.storage.persist()». Es decir: sin esta
 * llamada, somos de los primeros en caer.
 *
 * En una web normal eso es una molestia. Aquí no: en IndexedDB vive la COLA DE
 * OPERACIONES OFFLINE (`rois_offline_mutation_queue` y sus hermanas). Son las
 * actuaciones, accidentes y tramos que el operario ha registrado sin cobertura
 * y que todavía no han llegado al servidor. Trabajo real, hecho, sin enviar.
 * Que el navegador pueda borrarlo es exactamente lo que la cola existe para
 * evitar.
 *
 * Y es un riesgo perfectamente plausible en el escenario real: tablet de campo,
 * disco compartido con fotos y mapas, jornadas enteras sin abrir la app.
 *
 * ── Cuándo se concede ─────────────────────────────────────────────────────
 * Chrome no enseña ningún diálogo: decide solo, por heurísticas de uso
 * (PWA instalada, historial de visitas, permisos concedidos). Por eso NO basta
 * con pedirlo una vez al arrancar — la primera visita casi siempre lo deniega.
 * Se pide en dos momentos:
 *
 *   1. Al arrancar la app (oportunista, barato).
 *   2. Al encolar la primera operación offline — que es cuando de verdad
 *      importa, y además cuando el navegador ya nos considera "un sitio que
 *      esta persona usa".
 *
 * Firefox sí muestra un diálogo, así que ahí la respuesta depende del usuario.
 *
 * Nunca lanza: si el navegador no soporta la API o la deniega, la app sigue
 * exactamente igual que hasta ahora. Esto solo puede mejorar la situación.
 */

/** Memoriza el resultado para no repetir la llamada en cada encolado. */
let yaConcedido = false;

export async function ensurePersistentStorage(motivo: string): Promise<boolean> {
  try {
    // Navegadores sin StorageManager (o contextos no seguros): no hay nada que
    // pedir. No es un error — simplemente no se puede mejorar nada aquí.
    if (!navigator.storage?.persist || !navigator.storage?.persisted) {
      log('StorageManager no disponible (%s) — se sigue en best-effort', motivo);
      return false;
    }

    if (yaConcedido) return true;

    // Preguntar antes de pedir: si ya está concedido, `persist()` volvería a
    // resolver true, pero preguntar es más barato y deja el log más honesto.
    if (await navigator.storage.persisted()) {
      yaConcedido = true;
      log('almacenamiento ya era persistente (%s)', motivo);
      return true;
    }

    const concedido = await navigator.storage.persist();
    yaConcedido = concedido;
    log(
      concedido
        ? 'concedido (%s) — el navegador ya no desalojará la cola offline'
        : 'DENEGADO (%s) — seguimos en best-effort; se reintentará al encolar trabajo',
      motivo
    );
    return concedido;
  } catch (error) {
    // Que esto falle no puede romper el arranque ni el encolado.
    log('error al pedir almacenamiento persistente (%s): %o', motivo, error);
    return false;
  }
}

/**
 * Cuánto espacio ocupamos y cuánto nos deja el navegador. Solo para
 * diagnóstico (consola de la tablet, capturas de QA); no decide nada.
 */
export async function getStorageEstimate(): Promise<{
  usoMb: number;
  cuotaMb: number;
  porcentaje: number;
} | null> {
  try {
    if (!navigator.storage?.estimate) return null;
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return {
      usoMb: +(usage / 1024 / 1024).toFixed(2),
      cuotaMb: +(quota / 1024 / 1024).toFixed(2),
      porcentaje: quota ? +((usage / quota) * 100).toFixed(2) : 0,
    };
  } catch {
    return null;
  }
}
